/* eslint-disable react/display-name */
/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, FocusEvent, ForwardedRef, ChangeEvent, MouseEventHandler } from 'react';

import { withGoogleAnalyticsClickEvent, GoogleAnalyticsConstants } from '@vvoyages/google-analytics';
import classNames from 'classnames';
import Image from 'next/image';

import styles from './Input.module.scss';

type InputProps = {
  className?: string;
  bankCard?: string;
  LockIconPath?: string;
  label?: string;
  labelContainerClass?: string;
  inputContainerClass?: string;
  inputContainerErrorClass?: string;
  type?: string;
  value?: string;
  required?: boolean;
  disabled?: boolean;
  iconDisabled?: boolean;
  error?: boolean;
  placeholder?: string;
  maxVal?: number;
  name?: string;
  checkboxOverlayClass?: string;
  maxLength?: number;
  onChange?: Function;
  onBlur?: Function;
  onFocus?: Function;
  onClick?: MouseEventHandler<HTMLInputElement>;
};

const Input = React.forwardRef(
  (
    {
      className = '',
      LockIconPath = '',
      bankCard = '',
      label = '',
      labelContainerClass = '',
      type = '',
      value = '',
      required = false,
      disabled = false,
      iconDisabled = false,
      onChange = () => {},
      onBlur = () => {},
      onFocus = () => {},
      onClick,
      placeholder = '',
      inputContainerClass = '',
      inputContainerErrorClass = '',
      error = false,
      maxVal = 0,
      name = '',
      checkboxOverlayClass = '',
      maxLength = 0,
    }: InputProps,
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const [isChecked, setIsChecked] = useState(false);
    const valueInput = value || '';

    useEffect(() => {
      if (onClick) {
        setIsFocused(true);
      }
      if (labelContainerClass) {
        setIsFocused(false);
      }
    }, [onClick, labelContainerClass]);

    const blurHandler = (e: FocusEvent<HTMLInputElement>) => {
      e.preventDefault();
      setIsFocused(false);
      onFocus(e);
    };

    const focusHandler = (e: FocusEvent<HTMLInputElement>) => {
      e.preventDefault();
      setIsFocused(true);
      onBlur(e);
    };

    const changeHandler = (e: ChangeEvent<HTMLInputElement>, inputType: string) => {
      if (inputType === 'text' || inputType === 'number') {
        onChange(e.target.value);
        return;
      }

      if (ref != null && ref instanceof HTMLInputElement) {
        setIsChecked(ref.checked);
        onChange(e.target.checked);
      } else {
        onChange(e.target.value);
      }
    };

    const classes = classNames({
      [styles.input__label]: true,
      [styles['label-up']]:
        valueInput !== '' || valueInput.toString().trim().length > 0 || (isFocused && type !== 'checkbox'),
      [styles['label-checkbox']]: type === 'checkbox',
      [className]: true,
    });

    const inputClasses = classNames({
      [styles['input-section']]: true,
      [inputContainerClass]: !!inputContainerClass,
      [styles['input-error']]: error,
      [inputContainerErrorClass]: !!error,
      [className]: true,
    });

    return (
      <div className={classNames(styles.input, className)}>
        <label htmlFor={label} className={classNames(classes, labelContainerClass)}>
          {`${label}${required ? '*' : ''}`}
        </label>
        <div className={styles['relative-input']}>
          <input
            name={name}
            type={type}
            onBlur={blurHandler}
            onFocus={focusHandler}
            onChange={(event) => changeHandler(event, type)}
            placeholder={placeholder}
            value={valueInput}
            required={required}
            aria-required={required}
            disabled={disabled}
            ref={ref}
            min={1}
            max={maxVal}
            className={classNames(inputClasses)}
            autoComplete="off"
            step="any"
            onClick={onClick}
            maxLength={maxLength > 0 ? maxLength : undefined}
            id={label}
          />
          {type === 'checkbox' && isChecked && (
            <span className={classNames(styles.checkboxCustom, checkboxOverlayClass)} />
          )}
          <div className={styles['right-icons']}>
            {disabled && iconDisabled && <Image src={LockIconPath} alt="lock-icon" width={15} height={18} />}
            {bankCard && <Image src={bankCard} alt="bank-icon" width={50} height={30} />}
          </div>
        </div>
      </div>
    );
  },
);

export default withGoogleAnalyticsClickEvent(
  Input,
  () => GoogleAnalyticsConstants.EVENT_ACTION.FIELD_CLICK,
  (props: InputProps) => props.label,
);
